import { FormControl, FormGroup } from '@angular/forms';
import { CheckboxTreeControlConfig } from 'src/app/features/reactive-forms-ui/models/checkbox-tree-control-config';
import { CustomDropdownControlConfig } from 'src/app/features/reactive-forms-ui/models/custom-dropdown-control-config';
import { DatetimeRangeQuickPickerControlConfig } from 'src/app/features/reactive-forms-ui/models/datetime-range-quick-picker-control-config';
import { FiltersPanelLayoutConfig } from 'src/app/features/reactive-forms-ui/models/filters-panel-layout-config';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { OneLineTextControlConfig } from 'src/app/features/reactive-forms-ui/models/one-line-text-control-config';
import { TicketAnalysisFilterOption, TicketAnalysisFilters } from '../../models/ticket-analysis.models';

export function defaultDateFrom(): Date {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function createTicketAnalysisFiltersForm() {
  return new FormGroup({
    dateRange: new FormControl<{ start: Date; end: Date } | null>({
      start: defaultDateFrom(),
      end: new Date(),
    }),
    site: new FormControl(null),
    board: new FormControl<string[] | null>(null),
    contactName: new FormControl<string | null>(null),
    status: new FormControl(null),
    source: new FormControl(null),
  });
}

export function createTicketAnalysisFilterConfigMap(): Map<string, IFormControlConfig> {
  return new Map<string, IFormControlConfig>([
    [
      'dateRange',
      new DatetimeRangeQuickPickerControlConfig({
        collapsibleTitle: 'Time Range',
        reviewLabel: 'Time Range',
        startDateTimeLabel: 'Start date',
        endDateTimeLabel: 'End date',
        showTimeInputs: false,
        showCancelButton: true,
        selectedQuickOptionId: 'lastMonth',
        setDefaultValuesAtInit: true,
        startDateTime: defaultDateFrom().toISOString(),
        endDateTime: new Date().toISOString(),
        quickOptions: [
          { id: 'lastMonth', name: 'Last Month' },
          { id: 'last3Months', name: 'Last 3 Months' },
          { id: 'lastYear', name: 'Last Year' },
          { id: 'custom', name: 'Custom' },
        ],
      }),
    ],
    [
      'site',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Site',
        reviewLabel: 'Site',
        classifications: ['Sites'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter sites',
        items: [],
      }),
    ],
    [
      'board',
      new CustomDropdownControlConfig({
        collapsibleTitle: 'Board',
        label: 'Board',
        placeholder: 'All boards',
        reviewLabel: 'Board',
        options: [],
        multiselect: true,
      }),
    ],
    [
      'contactName',
      new OneLineTextControlConfig({
        collapsibleTitle: 'Contact Name',
        reviewLabel: 'Contact Name',
        placeholder: 'Search contact',
        showCancelIcon: true,
        trailingIcon: 'icon_search_gray',
      }),
    ],
    [
      'status',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Status',
        reviewLabel: 'Status',
        classifications: ['Statuses'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter statuses',
        items: [],
      }),
    ],
    [
      'source',
      new CheckboxTreeControlConfig({
        collapsibleTitle: 'Source',
        reviewLabel: 'Source',
        classifications: ['Sources'],
        showSearchBar: true,
        showSelectAll: true,
        searchPlaceholder: 'Filter sources',
        items: [],
      }),
    ],
  ]);
}

export const TICKET_ANALYSIS_FILTERS_LAYOUT = new FiltersPanelLayoutConfig({
  elementIds: ['dateRange', 'site', 'board', 'status', 'source'],
  title: 'Filters',
});

export function extractBaseFilters(
  valuesMap: any,
): Pick<TicketAnalysisFilters, 'dateFrom' | 'dateTo' | 'board' | 'site' | 'contactName' | 'status' | 'source'> {
  const dateRange = valuesMap?.dateRange as { start: Date; end: Date } | null;
  const board = valuesMap?.board;
  const boardValues: string[] = Array.isArray(board) ? board : [];
  const siteValues: string[] = valuesMap?.site?.['Sites']?.values ?? [];
  const statusValues: string[] = valuesMap?.status?.['Statuses']?.values ?? [];
  const sourceValues: string[] = valuesMap?.source?.['Sources']?.values ?? [];
  const contactName: string | undefined = valuesMap?.contactName || undefined;

  return {
    dateFrom: dateRange?.start ? toDateString(dateRange.start) : toDateString(defaultDateFrom()),
    dateTo: dateRange?.end ? toDateString(dateRange.end) : toDateString(new Date()),
    board: boardValues.length > 0 ? boardValues.join(',') : undefined,
    site: siteValues.length > 0 ? siteValues.join(',') : undefined,
    contactName,
    status: statusValues.length > 0 ? statusValues.join(',') : undefined,
    source: sourceValues.length > 0 ? sourceValues.join(',') : undefined,
  };
}

export function updateCheckboxTreeOptions(
  key: string,
  items: TicketAnalysisFilterOption[],
  configMap: Map<string, IFormControlConfig>,
): Map<string, IFormControlConfig> {
  const existing = configMap.get(key) as CheckboxTreeControlConfig;
  const updated = new CheckboxTreeControlConfig({
    ...(existing as any),
    items: items.map((i) => ({ id: i.name, displayName: i.name, children: [] })),
  });
  return new Map(configMap).set(key, updated);
}

export interface CsvColumn<T> {
  header: string;
  key: keyof T;
  format?: (value: any) => string;
}

export function exportToCsv<T extends Record<string, any>>(filename: string, rows: T[], columns: CsvColumn<T>[]): void {
  const header = columns.map((c) => `"${c.header}"`).join(',');
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const val = row[c.key];
          const str = c.format ? c.format(val) : val == null ? '' : String(val);
          return `"${str.replace(/"/g, '""')}"`;
        })
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function applyFilterDropdowns(
  filters: {
    boards?: TicketAnalysisFilterOption[];
    sites?: TicketAnalysisFilterOption[];
    statuses?: TicketAnalysisFilterOption[];
    sources?: TicketAnalysisFilterOption[];
  },
  configMap: Map<string, IFormControlConfig>,
): Map<string, IFormControlConfig> {
  let map = configMap;
  if (filters.boards?.length) map = updateBoardDropdownOptions(filters.boards, map);
  if (filters.sites?.length) map = updateCheckboxTreeOptions('site', filters.sites, map);
  if (filters.statuses?.length) map = updateCheckboxTreeOptions('status', filters.statuses, map);
  if (filters.sources?.length) map = updateCheckboxTreeOptions('source', filters.sources, map);
  return map;
}

function updateBoardDropdownOptions(
  boards: TicketAnalysisFilterOption[],
  configMap: Map<string, IFormControlConfig>,
): Map<string, IFormControlConfig> {
  const existing = configMap.get('board') as CustomDropdownControlConfig;
  const updated = new CustomDropdownControlConfig({
    ...(existing as any),
    options: boards.map((b) => ({ name: b.name, value: b.name })),
  });
  return new Map(configMap).set('board', updated);
}
